document.addEventListener("DOMContentLoaded", function () {
  const root = document.querySelector(".firearm-options-container");
  if (!root || !window.nffi) return;

  // When true, the merged "MODEL + LIGHT" list is presented as two dropdowns:
  //   #firearm-model-base  (visible)  -> base gun name
  //   #weapon-light        (visible)  -> light for that gun; "No Light" is
  //                                      first and default where applicable
  //   #firearm-model       (hidden mirror) -> unchanged: full name + SKU value
  // The mirror preserves every existing contract (SKU value, option text, the
  // `+` light-bearing sniff in the holster matcher, external change listeners).
  const SPLIT_LIGHT = root.dataset.splitLight === "true";

  const brandSelect = root.querySelector("#firearm-brand");
  const modelSelect = root.querySelector("#firearm-model"); // mirror when SPLIT_LIGHT
  const modelBaseSelect = root.querySelector("#firearm-model-base"); // null unless SPLIT_LIGHT
  const lightSelect = root.querySelector("#weapon-light"); // null unless SPLIT_LIGHT
  const lightError = root.querySelector("#weapon-light-error");
  const holsterColorSelect = root.querySelector("#holster-color");
  const holstersContainer = root.querySelector("#holsters-container");
  const selectedHolsterIdInput = root.querySelector("#selected-holster-id");
  const selectedHolsterSKUInput = root.querySelector("#selected-holster-sku");

  // Cache for the Holsters collection fallback fetch (see loadHolsterAddonsOnce).
  let holsterAddonsPromise = null;

  // brandId -> Map(baseName -> [model entries]); built only when SPLIT_LIGHT.
  let splitIndex = new Map();

  // -----------------------------
  // Selected labels near dropdown title
  // -----------------------------
  function initDropdownSelectedLabels(scope = document) {
    const wraps = Array.from(scope.querySelectorAll(".firearm-dropdowns"));

    wraps.forEach((wrap) => {
      const label = wrap.querySelector(".dropdown-label");
      const select = wrap.querySelector("select.dropdown-select");
      if (!label || !select) return;

      // Save base label text once (without trailing ": ...")
      if (!label.dataset.baseText) {
        label.dataset.baseText = (label.textContent || "")
          .trim()
          .replace(/:.*$/, "");
      }

      const baseText = label.dataset.baseText;

      const update = () => {
        const opt = select.options?.[select.selectedIndex];
        const selectedText = (opt?.textContent || "").trim();

        const isPlaceholder =
          !select.value ||
          select.value === "" ||
          (opt && opt.disabled) ||
          /^select\b/i.test(selectedText);

        label.textContent = isPlaceholder ? baseText : `${baseText}: ${selectedText}`;
      };

      update();
      select.removeEventListener("change", update); // safety
      select.addEventListener("change", update);
    });
  }

  // Export for bag-options.js to call after show/hide
  window.NFFI_FirearmOptions = window.NFFI_FirearmOptions || {};
  window.NFFI_FirearmOptions.initDropdownSelectedLabels = initDropdownSelectedLabels;

  // -----------------------------
  // Model name parsing (SPLIT_LIGHT only)
  // -----------------------------

  /**
   * Split a manifest model name into its base gun and its bundled light.
   *
   *   "Glock 42"                                    -> base "Glock 42",                       light ""
   *   "Glock 17/19/19X/45/47 (Optics ready)"        -> base "Glock 17/19/19X/45/47 (Optics ready)", light ""
   *   "Glock 17/19/19X/45/47 + TLR-1HL Light (Optics ready)"
   *                                                 -> base "Glock 17/19/19X/45/47 (Optics ready)", light "TLR-1HL Light"
   *
   * IMPORTANT: the manifest parks "(Optics ready)" at the END of the whole
   * string — after the light — even though it describes the GUN. It is peeled
   * off first and re-attached to the base, otherwise a bare entry and its own
   * light variants resolve to different bases and the gun appears twice in the
   * dropdown. Same treatment for the " (Light Bearing)" suffix that the
   * merged-bundle branch appends.
   *
   * Splits on the FIRST "+" (with or without surrounding spaces — the manifest
   * has both, e.g. "CZ P10C/P10S +TLR-7/A/X/HLX") so light names containing a
   * further plus stay intact.
   */
  function parseModelName(rawName) {
    let name = String(rawName || "").trim();
    let suffix = "";

    const lbMatch = name.match(/\s*\(\s*light\s+bearing\s*\)\s*$/i);
    if (lbMatch) {
      suffix = " (Light Bearing)" + suffix;
      name = name.slice(0, lbMatch.index).trim();
    }

    const orMatch = name.match(/\s*\(\s*optics\s*ready\s*\)\s*$/i);
    if (orMatch) {
      suffix = " (Optics ready)" + suffix;
      name = name.slice(0, orMatch.index).trim();
    }

    const sep = name.match(/\s*\+\s*/);
    if (!sep) {
      return { base: name + suffix, light: "" };
    }

    return {
      base: name.slice(0, sep.index).trim() + suffix,
      light: name.slice(sep.index + sep[0].length).trim(),
    };
  }

  /**
   * Canonical grouping key for a base gun name.
   *
   * The manifest does not spell base names consistently across a gun's own
   * entries — "CANIK METE MC9" / "CANIK Mete MC9" / "CANIK METE MC 9",
   * "CZ P-07/P-09" / "CZ P07/P09", "Sig Sauer P320F(M17) / P320C(M18)" vs the
   * unspaced form. Keying on the raw string would list those guns two or three
   * times. Case, whitespace and separator punctuation are therefore stripped
   * for grouping only; the displayed label is still a real manifest string.
   */
  function baseKey(base) {
    return String(base)
      .toLowerCase()
      .replace(/\(\s*optics\s*ready\s*\)/g, "")
      .replace(/\(\s*light\s+bearing\s*\)/g, "")
      .replace(/[\s\-–—._'"’”“/\\()&]/g, "");
  }

  // -----------------------------
  // Per-product model blocklist
  // -----------------------------
  // The chest platform reads the SAME manifest entry as the QAP bags —
  // resolveFirearmModelsManifestEntryForProduct() treats ",chest-holster," as a
  // bag product and resolves bagsSmall/Large/Xlarge from the bag size code. So
  // these guns cannot be dropped from the JSON without also dropping them from
  // every bag that shares that file. They are filtered at load time instead,
  // gated on the chest tag, and nothing else is touched.
  //
  // Entries are matched on the baseKey()-normalized BASE name, which means:
  //   - spelling drift in the manifest (case, spacing, quotes, hyphens, the
  //     trailing "(Optics ready)" / "(Light Bearing)") does not matter, and
  //   - any "+ <light>" variant of the same gun is removed too, since
  //     parseModelName() peels the light off before the key is taken.
  //
  // To add a gun: paste its display name here. To scope another product:
  // add a tag test in getBlockedModelKeys().
  const CHEST_MODEL_BLOCKLIST = [
    'Kimber K6 2" barrel',
    'Smith & Wesson J-Frame (up to 2.13" barrel length)',
    "Ruger LCR",
  ];

  /**
   * Blocklist for the CURRENT product, or null when nothing is blocked.
   * Chest holsters only — bags, IWB, velcro and every bundle keep the full list.
   */
  function getBlockedModelKeys() {
    const tags = (window.nffi.productTagsCsv || "").toLowerCase();
    if (!tags.includes(",chest-holster,")) return null;

    return CHEST_MODEL_BLOCKLIST.map((label) => ({
      label,
      key: baseKey(parseModelName(label).base),
    }));
  }

  /**
   * Drop blocked guns from a freshly fetched model list, before it is assigned
   * to window.nffi.firearm_models. Runs ahead of buildSplitIndex() and
   * populateBrands(), so a brand left with no models drops out of the Brand
   * dropdown on its own — populateBrands() already filters to brands that have
   * models.
   */
  function applyProductModelBlocklist(models) {
    const blocked = getBlockedModelKeys();
    if (!blocked || !Array.isArray(models)) return models || [];

    const blockedKeys = new Set(blocked.map((b) => b.key));
    const hitKeys = new Set();

    const kept = models.filter((model) => {
      const key = baseKey(parseModelName(model.name).base);
      if (blockedKeys.has(key)) {
        hitKeys.add(key);
        return false;
      }
      return true;
    });

    // A blocklist entry matching nothing means the manifest spells that gun
    // differently than the label above — the removal would silently no-op, so
    // it is called out instead. (The product description metafield is known to
    // drift from the live manifest.)
    blocked.forEach((b) => {
      if (!hitKeys.has(b.key)) {
        console.warn(
          "[firearm-options] blocklist entry matched no model — check the manifest spelling for:",
          b.label
        );
      }
    });

    return kept;
  }

  /**
   * Group window.nffi.firearm_models by brand, then by canonical base key.
   * Decorates each entry with __base / __light for later lookups.
   * Within a base, bare (no-light) entries are floated to the top so index 0 is
   * always the safest default. Array.prototype.sort is stable, so manifest
   * order is otherwise preserved.
   */
  function buildSplitIndex() {
    splitIndex = new Map();

    (window.nffi.firearm_models || []).forEach((model) => {
      const parsed = parseModelName(model.name);
      model.__base = parsed.base;
      model.__light = parsed.light;
      model.__baseKey = baseKey(parsed.base);

      const brandKey = String(model.brandId);
      if (!splitIndex.has(brandKey)) splitIndex.set(brandKey, new Map());

      const byBase = splitIndex.get(brandKey);
      if (!byBase.has(model.__baseKey)) byBase.set(model.__baseKey, []);
      byBase.get(model.__baseKey).push(model);
    });

    splitIndex.forEach((byBase) => {
      byBase.forEach((entries) => {
        entries.sort((a, b) => (a.__light ? 1 : 0) - (b.__light ? 1 : 0));
      });
    });
  }

  /**
   * Label to show for a group: prefer the bare entry's spelling (it is the one
   * written without a light appended), otherwise fall back to the first entry.
   */
  function groupLabel(entries) {
    const bare = entries.find((e) => !e.__light);
    return (bare || entries[0]).__base;
  }

  // -----------------------------
  // Load JSON data
  // -----------------------------

  initializeFirearmDataFromManifest(root);

  // Kick off the Holsters fallback load up front (no-op if the metafield is
  // already populated or this product has no holster picker). When it resolves,
  // re-fire the color change so an already-selected color resolves its holster.
  loadHolsterAddonsOnce().then(() => {
    if (holsterColorSelect && holsterColorSelect.value) {
      holsterColorSelect.dispatchEvent(new Event("change"));
    }
  });


    window.nffi.firearm_brands = window.nffi.firearm_brands || [];
    window.nffi.firearm_models = window.nffi.firearm_models || [];

    /**
     * Fetch JSON from Shopify Files.
     * Throws an error if the URL is empty, the HTTP response is not successful,
     * or the response body is not valid JSON.
     */
    async function fetchFirearmJsonFromShopifyFiles(url, contextLabel) {
      if (!url) {
        throw new Error(`${contextLabel}: URL is empty.`);
      }

      const response = await fetch(url, { cache: "no-store" });

      if (!response.ok) {
        throw new Error(
          `${contextLabel}: request failed with status ${response.status}.`
        );
      }

      return await response.json();
    }

    /**
     * Fetch JSON from the current file first.
     * If that fails, try the fallback file.
     */
    async function fetchFirearmJsonWithFallbackFromShopifyFiles(
      currentUrl,
      fallbackUrl,
      contextLabel
    ) {
      try {
        return await fetchFirearmJsonFromShopifyFiles(
          currentUrl,
          `${contextLabel} (current)`
        );
      } catch (currentError) {
        console.warn(
          `${contextLabel}: failed to load current file, trying fallback.`,
          currentError
        );

        if (!fallbackUrl) {
          throw currentError;
        }

        return await fetchFirearmJsonFromShopifyFiles(
          fallbackUrl,
          `${contextLabel} (fallback)`
        );
      }
    }

    /**
     * Resolve the correct firearm models manifest entry for the current product.
     */
    function resolveFirearmModelsManifestEntryForProduct(manifest) {
      const productTagsCsv = (window.nffi.productTagsCsv || "").toLowerCase();
      const bagSizeCode = normalizeFirearmBagSizeCode(window.nffi.bagSizeCode);

      const isBagProduct =
        productTagsCsv.includes(",bags,") ||
        productTagsCsv.includes(",bundle-bag-hlst,") ||
        productTagsCsv.includes(",chest-holster,");

      const isHolsterProduct =
        productTagsCsv.includes(",holsters,") ||
        productTagsCsv.includes(",bundle-hlst,");

      if (isBagProduct) {
        if (bagSizeCode.includes("XL")) {
          return manifest.models?.bagsXlarge;
        }

        if (bagSizeCode.includes("L")) {
          return manifest.models?.bagsLarge;
        }

        if (bagSizeCode.includes("S")) {
          return manifest.models?.bagsSmall;
        }

        throw new Error(
          "Bag size could not be resolved for firearm models manifest entry."
        );
      }

      if (isHolsterProduct) {
        const isVelcro = productTagsCsv.includes("velcro");
        const isIwb = productTagsCsv.includes("iwb");
        const isLightBearing = productTagsCsv.includes(",light bearing,");
        const isRegular = productTagsCsv.includes(",regular,");

        if (isVelcro && isLightBearing) {
          return manifest.models?.holstersVelcroLightBearing;
        }

        if (isVelcro && isRegular) {
          return manifest.models?.holstersVelcroRegular;
        }

        if (isIwb && isLightBearing) {
          return manifest.models?.holstersIwbLightBearing;
        }

        if (isIwb && isRegular) {
          return manifest.models?.holstersIwbRegular;
        }

        throw new Error(
          "Holster type could not be resolved for firearm models manifest entry."
        );
      }

      throw new Error(
        "Firearm models manifest entry could not be resolved for the current product."
      );
    }

    /**
     * Returns an array of model sources when a product should show a MERGED
     * firearm list — a BUNDLE tagged with a holster type AND both regular and
     * light bearing (e.g. the Flex Bundle: IWB + Regular + Light Bearing).
     * Returns null otherwise, so every single-type product (including all
     * holsters on the Holsters page) keeps its existing single-set behavior.
     */
    function resolveMergedHolsterModelSourcesForProduct(manifest) {
      const tags = (window.nffi.productTagsCsv || "").toLowerCase();

      const isBundle = tags.includes(",bundle,");
      const isVelcro = tags.includes("velcro");
      const isIwb = tags.includes("iwb");
      const wantsRegular = tags.includes(",regular,");
      const wantsLight = tags.includes(",light bearing,");

      // Only merge for a bundle tagged with a holster type AND both regular
      // + light bearing. The bundle guard ensures the Holsters page can never
      // be affected, even by an accidentally double-tagged holster.
      if (!isBundle || !(isVelcro || isIwb) || !(wantsRegular && wantsLight)) {
        return null;
      }

      const models = manifest.models || {};
      const regularEntry = isVelcro
        ? models.holstersVelcroRegular
        : models.holstersIwbRegular;
      const lightEntry = isVelcro
        ? models.holstersVelcroLightBearing
        : models.holstersIwbLightBearing;

      const sources = [];
      if (regularEntry) sources.push({ entry: regularEntry, isLightBearing: false });
      if (lightEntry) sources.push({ entry: lightEntry, isLightBearing: true });

      return sources.length === 2 ? sources : null;
    }

    /**
     * Show a graceful error state instead of leaving the UI half-broken.
     */
    function handleFirearmDataInitializationError(rootElement, error) {
      const errorContainer = rootElement.querySelector("[data-firearm-data-error]");
      if (errorContainer) {
        errorContainer.textContent =
          "Firearm model data is temporarily unavailable. Please try again later.";
        errorContainer.style.display = "block";
      }

      const selectorsContainer = rootElement.querySelector(
        "[data-firearm-selectors]"
      );
      if (selectorsContainer) {
        selectorsContainer.style.display = "none";
      }

      console.error("Failed to initialize firearm data from manifest:", error);
    }

    /**
     * Validate the minimal manifest structure required by the storefront.
     */
    function validateFirearmManifestStructure(manifest) {
      if (!manifest || typeof manifest !== "object") {
        throw new Error("Firearm manifest is missing or invalid.");
      }

      if (!manifest.brands || !manifest.models) {
        throw new Error("Firearm manifest does not contain required sections.");
      }

      if (!manifest.brands.currentUrl && !manifest.brands.fallbackUrl) {
        throw new Error("Firearm brands manifest entry does not contain usable URLs.");
      }
    }

    /**
     * Initialize firearm brands and models from the Shopify manifest file.
     */
    async function initializeFirearmDataFromManifest(rootElement) {
      try {
        const firearmManifest = await fetchFirearmJsonFromShopifyFiles(
          window.nffi.firearmManifestJsonUrl,
          "firearm manifest"
        );

        validateFirearmManifestStructure(firearmManifest);

        // --- Merged holster list (regular + light bearing) ---
        // Bundles tagged with a holster type + both Regular and Light Bearing
        // get a single dropdown combining both model sets. Light-bearing models
        // are flagged (__lightBearing) and suffixed " (Light Bearing)".
        const mergedSources =
          resolveMergedHolsterModelSourcesForProduct(firearmManifest);

        if (mergedSources) {
          const fetched = await Promise.all([
            fetchFirearmJsonWithFallbackFromShopifyFiles(
              firearmManifest.brands.currentUrl,
              firearmManifest.brands.fallbackUrl,
              "firearm brands"
            ),
            ...mergedSources.map((src) =>
              fetchFirearmJsonWithFallbackFromShopifyFiles(
                src.entry.currentUrl,
                src.entry.fallbackUrl,
                "firearm models (" +
                  (src.isLightBearing ? "light-bearing" : "regular") +
                  ")"
              )
            ),
          ]);

          const mergedModels = [];
          fetched.slice(1).forEach(function (list, i) {
            const isLB = mergedSources[i].isLightBearing;
            (list || []).forEach(function (m) {
              const copy = Object.assign({}, m);
              copy.__lightBearing = isLB;
              if (isLB) copy.name = (m.name || "") + " (Light Bearing)";
              mergedModels.push(copy);
            });
          });

          window.nffi.firearm_brands = fetched[0];
          window.nffi.firearm_models = applyProductModelBlocklist(mergedModels);

          if (SPLIT_LIGHT) buildSplitIndex();

          populateBrands();
          initDropdownSelectedLabels(rootElement);
          return;
        }
        // --- end merged branch ---

        const firearmModelsManifestEntry =
          resolveFirearmModelsManifestEntryForProduct(firearmManifest);

        if (
          !firearmModelsManifestEntry ||
          (!firearmModelsManifestEntry.currentUrl &&
            !firearmModelsManifestEntry.fallbackUrl)
        ) {
          throw new Error(
            "Firearm models manifest entry does not contain usable URLs."
          );
        }

        const [firearmBrands, firearmModels] = await Promise.all([
          fetchFirearmJsonWithFallbackFromShopifyFiles(
            firearmManifest.brands.currentUrl,
            firearmManifest.brands.fallbackUrl,
            "firearm brands"
          ),
          fetchFirearmJsonWithFallbackFromShopifyFiles(
            firearmModelsManifestEntry.currentUrl,
            firearmModelsManifestEntry.fallbackUrl,
            "firearm models"
          ),
        ]);

        window.nffi.firearm_brands = firearmBrands;
        window.nffi.firearm_models = applyProductModelBlocklist(firearmModels);

        if (SPLIT_LIGHT) buildSplitIndex();

        populateBrands();
        initDropdownSelectedLabels(rootElement);
      } catch (error) {
        window.nffi.firearm_brands = [];
        window.nffi.firearm_models = [];
        splitIndex = new Map();

        handleFirearmDataInitializationError(rootElement, error);
      }
    }

    function normalizeFirearmBagSizeCode(rawBagSizeCode) {
      if (typeof rawBagSizeCode === "string") {
        return rawBagSizeCode.toUpperCase();
      }

      if (Array.isArray(rawBagSizeCode)) {
        const firstValue = rawBagSizeCode.find(
          (item) => typeof item === "string" && item.trim() !== ""
        );

        return firstValue ? firstValue.toUpperCase() : "";
      }

      if (rawBagSizeCode == null) {
        return "";
      }

      return String(rawBagSizeCode).toUpperCase();
    }

    // -----------------------------
    // Holsters fallback loader
    // -----------------------------
    // Some products (notably Shopify-duplicated bags) do not copy the
    // product-reference metafield, so window.nffi.product_addons.Holsters
    // arrives empty. Without holsters the color matcher can never resolve
    // #selected-holster-id, and BAGS WITH HOLSTER add-to-cart validation
    // blocks (it scrolls to #holster-options). When the metafield is empty we
    // fetch the Holsters collection directly and normalize it into the SAME
    // shape the metafield provides, so the existing matcher / renderHolster /
    // price code all work without change:
    //   price  -> integer cents
    //   images -> array of string URLs
    //   tags   -> array of strings
    function loadHolsterAddonsOnce() {
      if (!holsterAddonsPromise) {
        holsterAddonsPromise = ensureHolsterAddonsLoaded();
      }
      return holsterAddonsPromise;
    }

    async function ensureHolsterAddonsLoaded() {
      window.nffi.product_addons = window.nffi.product_addons || {};

      const existing = window.nffi.product_addons.Holsters;
      if (Array.isArray(existing) && existing.length > 0) {
        return existing;
      }

      // Only relevant where a holster color picker is present (Bags / Chest).
      if (!holsterColorSelect) {
        return existing || [];
      }

      try {
        const all = [];
        const LIMIT = 250;

        for (let page = 1; page <= 10; page++) {
          const res = await fetch(
            `/collections/holsters/products.json?limit=${LIMIT}&page=${page}`,
            { cache: "no-store" }
          );
          if (!res.ok) break;

          const data = await res.json();
          const list = Array.isArray(data.products) ? data.products : [];
          if (list.length === 0) break;

          all.push(...list);
          if (list.length < LIMIT) break;
        }

        const toCents = (dollarString) =>
          Math.round(parseFloat(dollarString || "0") * 100);

        const normalizeImageSrc = (img) => {
          const src = typeof img === "string" ? img : (img && img.src) || "";
          return src.startsWith("//") ? "https:" + src : src;
        };

        const normalized = all.map((p) => ({
          id: p.id,
          title: p.title,
          handle: p.handle,
          tags: Array.isArray(p.tags)
            ? p.tags
            : String(p.tags || "")
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean),
          // products.json variant price is a dollar string -> store cents to
          // match the metafield (renderHolster divides by 100).
          price:
            p.variants && p.variants[0] ? toCents(p.variants[0].price) : 0,
          variants: Array.isArray(p.variants)
            ? p.variants.map((v) => ({
                id: v.id,
                sku: v.sku,
                price: toCents(v.price),
              }))
            : [],
          images: Array.isArray(p.images) ? p.images.map(normalizeImageSrc) : [],
        }));

        window.nffi.product_addons.Holsters = normalized;
        return normalized;
      } catch (err) {
        console.error(
          "[ensureHolsterAddonsLoaded] failed to load holsters collection:",
          err
        );
        return existing || [];
      }
    }

  function populateBrands() {
    if (!brandSelect) return;

    const brandIdsWithModels = new Set(window.nffi.firearm_models.map((m) => m.brandId));
    window.nffi.firearm_brands
      .filter((brand) => brandIdsWithModels.has(brand.id))
      .forEach((brand) => {
        const option = document.createElement("option");
        option.value = brand.id;
        option.textContent = brand.name;
        option.dataset.label = brand.name;
        brandSelect.appendChild(option);
      });

    initDropdownSelectedLabels(root);
  }

  function populateModels(brandId) {
    if (!modelSelect) return;

    const filtered = window.nffi.firearm_models.filter(
      (m) => m.brandId.toString() === brandId.toString()
    );

    if (SPLIT_LIGHT && modelBaseSelect) {
      // The hidden mirror keeps the FULL merged list exactly as before: option
      // value = SKU, option text = full "MODEL + LIGHT" name. Nothing reading
      // #firearm-model needs to know the UI was split.
      modelSelect.innerHTML = '<option value="" disabled selected></option>';
      filtered.forEach((model) => {
        const option = document.createElement("option");
        option.value = model.sku;
        option.textContent = model.name;
        option.dataset.label = model.name;
        modelSelect.appendChild(option);
      });
      modelSelect.value = "";

      // The visible select gets deduped base gun names only.
      modelBaseSelect.innerHTML =
        '<option value="" disabled selected>Select Model</option>';

      const byBase = splitIndex.get(String(brandId)) || new Map();
      byBase.forEach((entries, key) => {
        const option = document.createElement("option");
        option.value = key; // canonical key; text is the manifest spelling
        option.textContent = groupLabel(entries);
        modelBaseSelect.appendChild(option);
      });

      modelBaseSelect.disabled = byBase.size === 0;
      resetLights();
      initDropdownSelectedLabels(root);
      return;
    }

    modelSelect.innerHTML = '<option value="" disabled selected>Select Model</option>';

    filtered.forEach((model) => {
      const option = document.createElement("option");
      option.value = model.sku; // NOTE: SKU in value, text is Name
      option.textContent = model.name;
      option.dataset.label = model.name;
      modelSelect.appendChild(option);
    });

    modelSelect.disabled = filtered.length === 0;
    initDropdownSelectedLabels(root);
  }

  // -----------------------------
  // SPLIT_LIGHT helpers
  // -----------------------------

  function resetLights() {
    if (!lightSelect) return;
    lightSelect.innerHTML =
      '<option value="" disabled selected>Select Weapon Light</option>';
    lightSelect.disabled = true;
  }

  /**
   * Fill #weapon-light for a base gun.
   *
   * "No Light" is offered only where it is actually applicable — i.e. where the
   * manifest has a bare entry for the gun, carrying that entry's SKU. It is
   * then first and default, so a customer is never silently dropped into a
   * light bundle they did not ask for, and can always come back to it after
   * trying a light.
   *
   * The 14 guns in Bags_Large.json that exist ONLY bundled with a light get a
   * "Select Weapon Light" placeholder instead. No dead "No Light" option that
   * resolves to nothing: the light is a real requirement for those, so the
   * customer is asked to pick one rather than being offered a choice that
   * cannot be fulfilled. Nothing is auto-selected on their behalf.
   *
   * Returns the bare entry, or null when the gun has no no-light option.
   */
  function populateLights(brandId, baseKeyValue) {
    if (!lightSelect) return null;

    const byBase = splitIndex.get(String(brandId));
    const entries = (byBase && byBase.get(baseKeyValue)) || [];
    const bare = entries.find((entry) => !entry.__light) || null;

    lightSelect.innerHTML = "";

    if (bare) {
      const noLightOption = document.createElement("option");
      noLightOption.value = bare.sku;
      noLightOption.textContent = "No Light";
      lightSelect.appendChild(noLightOption);
    } else {
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = "Select Weapon Light";
      lightSelect.appendChild(placeholder);
    }

    entries
      .filter((entry) => entry.__light)
      .forEach((entry) => {
        const option = document.createElement("option");
        option.value = entry.sku;
        option.textContent = entry.__light;
        lightSelect.appendChild(option);
      });

    lightSelect.disabled = entries.length === 0;
    lightSelect.selectedIndex = 0; // "No Light", or the placeholder

    return bare;
  }

  /**
   * Show/hide the weapon light error.
   *
   * Needed because custom.product-info.js validates #firearm-model (the mirror)
   * and, when a light-only gun has no light picked, the mirror is empty — so its
   * message reads "Please select Firearm Model" while a model is plainly
   * selected. This surfaces the real reason next to the real control.
   */
  function setLightError(show) {
    if (!lightError) return;
    lightError.style.display = show ? "inline-block" : "none";
  }

  // Their submit handler is bound on document in the capture phase and calls
  // validateSelections(). This runs alongside it rather than replacing it: it
  // only paints the light error, and never blocks on its own — the empty mirror
  // already blocks add-to-cart.
  document.addEventListener(
    "submit",
    function (e) {
      if (!SPLIT_LIGHT || !lightSelect) return;
      if (!e.target.closest || !e.target.closest("form.shopify-product-form")) return;

      const modelChosen = !!(modelBaseSelect && modelBaseSelect.value);
      setLightError(modelChosen && !lightSelect.value);
    },
    true
  );

  /**
   * Point the hidden mirror at a SKU and fan the change out to everything that
   * listens on #firearm-model. No internal listener is bound to the mirror in
   * split mode, so this cannot recurse.
   */
  function applyMirrorSku(sku) {
    if (!modelSelect) return;
    modelSelect.value = sku || "";
    modelSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function clearHolsterSelection() {
    if (holstersContainer) {
      holstersContainer.innerHTML = "";
      holstersContainer.style.display = "none";
    }
    if (selectedHolsterIdInput) selectedHolsterIdInput.value = "";
    if (selectedHolsterSKUInput) selectedHolsterSKUInput.value = "";
  }

  // -----------------------------
  // Brand change
  // -----------------------------
  if (brandSelect) {
    brandSelect.addEventListener("change", function () {
      if (holsterColorSelect) holsterColorSelect.selectedIndex = 0;

      if (!this.value) {
        if (SPLIT_LIGHT && modelBaseSelect) {
          modelBaseSelect.disabled = true;
          modelBaseSelect.innerHTML =
            '<option value="" disabled selected>Select Model</option>';
          resetLights();
          setLightError(false);
          applyMirrorSku("");
        } else if (modelSelect) {
          modelSelect.disabled = true;
          modelSelect.innerHTML = '<option value="" disabled selected>Select Model</option>';
        }
        if (holstersContainer) holstersContainer.style.display = "none";
      } else {
        populateModels(this.value);

        if (SPLIT_LIGHT) applyMirrorSku("");

        if (holstersContainer && holsterColorSelect) {
          holsterColorSelect.disabled = true;
          holstersContainer.style.display = "none";
        }
      }

      initDropdownSelectedLabels(root);

      document.dispatchEvent(new CustomEvent("nffi:firearm-options:changed"));
    });
  }

  // -----------------------------
  // Model change — SPLIT_LIGHT (base gun + weapon light)
  // -----------------------------
  if (SPLIT_LIGHT && modelBaseSelect) {
    modelBaseSelect.addEventListener("change", function () {
      const brandId = brandSelect ? brandSelect.value : "";
      const bare = populateLights(brandId, this.value);

      // Default to "No Light" where the gun has one; otherwise nothing is
      // selected and the customer picks a light.
      applyMirrorSku(bare ? bare.sku : "");
      setLightError(false);
      initDropdownSelectedLabels(root);

      clearHolsterSelection();

      if (holsterColorSelect) {
        holsterColorSelect.disabled = !modelSelect.value;
        holsterColorSelect.selectedIndex = 0;
        holsterColorSelect.dispatchEvent(new Event("change"));
      }

      document.dispatchEvent(new CustomEvent("nffi:firearm-options:changed"));
    });

    if (lightSelect) {
      lightSelect.addEventListener("change", function () {
        // Light flips the resolved SKU, which flips the REGULAR /
        // LIGHT BEARING tag the holster matcher wants — so the holster has to
        // re-resolve. The customer's colour choice is deliberately preserved.
        //
        // custom.product-info.js binds its own listener to #firearm-model that
        // blanks holsterColorSelect.value on every change. The mirror dispatch
        // below therefore wipes the colour as a side effect, so it is saved and
        // restored around the call. Without this the re-resolve no-ops,
        // #selected-holster-id stays empty and add-to-cart validation blocks.
        const keepColor = holsterColorSelect ? holsterColorSelect.value : "";

        applyMirrorSku(this.value);
        setLightError(false);

        if (holsterColorSelect) {
          if (modelSelect.value) {
            if (keepColor) holsterColorSelect.value = keepColor;
            holsterColorSelect.disabled = false;
          } else {
            // "No Light" on a light-only gun: nothing resolves, so do not leave
            // a colour attached to an empty model.
            holsterColorSelect.value = "";
            holsterColorSelect.disabled = true;
          }
        }

        initDropdownSelectedLabels(root);
        clearHolsterSelection();

        if (holsterColorSelect && holsterColorSelect.value) {
          holsterColorSelect.dispatchEvent(new Event("change"));
        }

        document.dispatchEvent(new CustomEvent("nffi:firearm-options:changed"));
      });
    }
  }

  // -----------------------------
  // Model change — legacy single merged dropdown
  // -----------------------------
  if (!SPLIT_LIGHT && modelSelect && holsterColorSelect) {
    modelSelect.addEventListener("change", function () {
      const selectedModel = this.value;
      holsterColorSelect.disabled = !selectedModel;
      holsterColorSelect.selectedIndex = 0;

      initDropdownSelectedLabels(root);

      // Clear holster card on model change
      if (holstersContainer) {
        holstersContainer.innerHTML = "";
        holstersContainer.style.display = "none";
      }

      // Also clear hidden selected holster (prevents stale selection)
      if (selectedHolsterIdInput) selectedHolsterIdInput.value = "";
      if (selectedHolsterSKUInput) selectedHolsterSKUInput.value = "";

      // Trigger color change to refresh (keeps your behavior)
      holsterColorSelect.dispatchEvent(new Event("change"));

      document.dispatchEvent(new CustomEvent("nffi:firearm-options:changed"));
    });
  }

  // -----------------------------
  // Holster color change
  // -----------------------------
  if (holsterColorSelect && holstersContainer) {
    holsterColorSelect.addEventListener("change", async function () {
      const selectedColor = this.value;
      holstersContainer.innerHTML = "";

      // Always update the label "Holster color: X"
      initDropdownSelectedLabels(root);

      if (!selectedColor) {
        holstersContainer.style.display = "none";
        if (selectedHolsterIdInput) selectedHolsterIdInput.value = "";
        if (selectedHolsterSKUInput) selectedHolsterSKUInput.value = "";        
        return;
      }

      const modelVal = modelSelect?.value || "";
      const isLightBearing = modelVal.includes("+");
      const selectedColorLower = selectedColor.toLowerCase().replaceAll(" ", "-");
      const desiredTag = isLightBearing ? "LIGHT BEARING" : "REGULAR";

      // Holster mount type depends on the product. The firearm picker only
      // renders for Bags or Chest-Holster: bags take a VELCRO-backed holster,
      // chest holsters take a DIRECT MOUNT holster.
      const productTagsCsv = (window.nffi.productTagsCsv || "").toLowerCase();
      const isChestProduct = productTagsCsv.includes(",chest-holster,");
      const requiredMountTag = isChestProduct ? "DIRECT MOUNT" : "VELCRO";

      // Ensure holsters are present (fallback fetch if the metafield was empty).
      await loadHolsterAddonsOnce();

      const holsters = window.nffi?.product_addons?.Holsters || [];
      const selectedHolster = holsters.find((h) => {
        const handleLower = (h.handle || "").toLowerCase();
        const tags = Array.isArray(h.tags)
          ? h.tags.map((t) => String(t).toUpperCase())
          : String(h.tags || "").toUpperCase();

        const matchesMount = tags.includes(requiredMountTag);
        const matchesTag = tags.includes(desiredTag);
        const matchesColor = handleLower.includes(selectedColorLower);

        return matchesMount && matchesTag && matchesColor;
      });

      if (selectedHolster) {
        renderHolster(selectedHolster);
      } else {
        holstersContainer.style.display = "none";
      }

      if (selectedHolsterIdInput) {
        selectedHolsterIdInput.value = selectedHolster?.variants?.[0]?.id || "";
      }
      if (selectedHolsterSKUInput) {
        selectedHolsterSKUInput.value = selectedHolster?.variants?.[0]?.sku || "";
      }

      const holsterPriceInput = document.querySelector(".firearm-options-container #selected-holster-price-cents");
      if (holsterPriceInput) {
        holsterPriceInput.value = selectedHolster ? String(selectedHolster.price || 0) : "";
      }

      const selectedHolsterNameInput = root.querySelector(".firearm-options-container #selected-holster-name");
      if(selectedHolsterNameInput){
        selectedHolsterNameInput.value = selectedHolster ? selectedHolster.title : "";
      }
console.log('[selectedHolster]', selectedHolster);
      // notify other scripts that holster selection changed
      document.dispatchEvent(new CustomEvent("nffi:holster-selected", selectedHolster));

    });
  }

  function renderHolster(holster) {
    holstersContainer.innerHTML = "";

    const image = holster.images && holster.images.length > 0 ? holster.images[0] : "";

    const productCard = document.createElement("product-card");
    productCard.className = "product-card bg-custom text-custom";
    productCard.setAttribute(
      "style",
      "--background: 224 224 224; --text-color: 26 26 26; opacity: 1; transform: translateY(0px);"
    );

    productCard.innerHTML = `
      <div class="product-card__figure" data-country="USA">
        <img 
          src="${image}" 
          alt="${String(holster.title || "").toLowerCase()}" 
          width="2500" height="2500"
          class="product-card__image product-card__image--primary aspect-square"
          loading="lazy"
        >
      </div>
    `;

    holstersContainer.appendChild(productCard);
    holstersContainer.style.display = "block";
  }
});